'use client';

import classnames from 'classnames';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React, { CSSProperties, useMemo, useRef, useState } from 'react';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { PurchaseReasonEnum } from '@cineamo/api/constants/PurchaseReason.enum';

import { onTicketButtonClick } from '@cineamo/api/helpers/ticket-purchase-intent-helper';

import { getHoursUntilShowing, isShowingSalePending } from '@cineamo/ui/src/helpers/showing-helper';
import { isDatetimePast } from '@cineamo/ui/src/helpers/time-helper';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { NoOnlineTicketUrlModal } from '../modals/NoOnlineTicketUrlModal';

import TicketShape from './TicketShape';
export enum TicketButtonSimpleStyle {
    Default,
    Light,
    OnWhite,
    OnDark
}

type TicketButtonSimpleProps = {
    className?: string;
    showing: ShowingDto;
    onlineTicketUrl?: string;
    purchaseReason?: PurchaseReasonEnum;
    children?: string | React.JSX.Element;
    ticketHeight?: 'auto' | 36 | 48;
    style?: TicketButtonSimpleStyle | CSSProperties;
    cornerRadius?: number;
    trackingData?: Record<string, string>;
    /**
     * Fired when a click actually starts the ticket flow (not disabled, online ticket URL
     * present) — the hook for app-level analytics like Pulse ticket_purchase_intent.
     */
    onTicketClick?: (showing: ShowingDto, isExternal: boolean) => void;
};

export function TicketButtonSimple(props: TicketButtonSimpleProps) {
    const { showing, onlineTicketUrl, purchaseReason, style, cornerRadius, trackingData, onTicketClick } = props;

    const t = useTranslations();
    const router = useRouter();
    const { track } = useAnalytics();

    const [noOnlineTicketUrlIsOpen, setNoOnlineTicketUrlIsOpen] = useState<boolean>(false);

    const showingId = showing.id;
    const datetime = showing.startDatetime;
    // Disable when the showing has (nearly) started, or when the ticket system's
    // online sale has not opened yet (e.g. Cinetixx `startSale` in the future).
    const isTicketClickDisabled = isDatetimePast(datetime, true) || isShowingSalePending(showing);
    const ticketLink = useMemo(() => {
        return {
            href: onlineTicketUrl ?? '',
            target: onlineTicketUrl?.startsWith('/') ? '_self' : '_blank',
            hasOnlineTicketUrl: !!onlineTicketUrl
        };
    }, [onlineTicketUrl]);

    const openingOnlineTicketUrlRef = useRef(false);

    // A CSSProperties `style` carries raw colors (themed apps and the embedded showtimes-finder
    // widget derive them from CSS vars), while the enum variants carry Tailwind class names.
    // The two cannot share a render path: a hex in `className` produces no class at all.
    const hasCustomStyle = typeof props.style === 'object';

    let ticketItemBgColorHex: string | undefined =
        typeof props.style === 'object' ? props.style.backgroundColor : undefined;
    let ticketItemBgOpacity: number = typeof props.style === 'object' ? 1 : 0.2;
    let ticketItemTextColor: string | undefined = typeof props.style === 'object' ? props.style.color : undefined;
    let ticketItemUnavailableBgColorHex: string | undefined =
        typeof props.style === 'object' ? props.style.backgroundColor : undefined;
    let ticketItemUnavailableBgOpacity: number = typeof props.style === 'object' ? 1 : 0.5;
    let ticketItemUnavailableTextColor: string = 'opacity-50';

    switch (style) {
        case TicketButtonSimpleStyle.Light:
            ticketItemBgColorHex = '#F2F2F2'; //tailwindColors.white.DEFAULT;
            ticketItemBgOpacity = 0.2;
            ticketItemTextColor = 'text-white';
            ticketItemUnavailableBgColorHex = '#887D9A'; //tailwindColors.violetGrey.DEFAULT;
            ticketItemUnavailableBgOpacity = 0.5;
            ticketItemUnavailableTextColor = 'text-white/80';
            break;
        case TicketButtonSimpleStyle.OnWhite:
            ticketItemBgColorHex = '#43DCC7'; //tailwindColors.cyan.middle;
            ticketItemBgOpacity = 1;
            ticketItemTextColor = 'text-darkBlue';
            ticketItemUnavailableBgColorHex = '#F2F2F2'; //tailwindColors.gray.light;
            ticketItemUnavailableBgOpacity = 1;
            ticketItemUnavailableTextColor = 'text-gray-middle';
            break;
        case TicketButtonSimpleStyle.OnDark:
            ticketItemBgColorHex = '#F2F2F233'; //tailwindColors.cyan.middle;
            ticketItemBgOpacity = 1;
            ticketItemTextColor = 'text-white';
            ticketItemUnavailableBgColorHex = '#f2f2f226'; //tailwindColors.gray.light;
            ticketItemUnavailableBgOpacity = 1;
            ticketItemUnavailableTextColor = 'text-gray-middle';
            break;
        case TicketButtonSimpleStyle.Default:
        case undefined:
            ticketItemBgColorHex = '#00FFDC'; //tailwindColors.cyan.DEFAULT;
            ticketItemBgOpacity = 1;
            ticketItemTextColor = 'text-darkBlue';
            ticketItemUnavailableBgColorHex = '#887D9A'; //tailwindColors.violetGrey.DEFAULT;
            ticketItemUnavailableBgOpacity = 1;
            ticketItemUnavailableTextColor = 'text-darkBlue';
            break;
    }

    let ticketHeightProp: string;
    switch (props.ticketHeight) {
        case 36:
            ticketHeightProp = '!h-[36px]';
            break;
        case 48:
            ticketHeightProp = '!h-[48px]';
            break;
        default:
            ticketHeightProp = '!h-auto';
            break;
    }

    function onTicketButtonClickHandler() {
        if (openingOnlineTicketUrlRef.current) return;
        openingOnlineTicketUrlRef.current = true;

        if (ticketLink.target === '_self') {
            // For internal links, wait for tracking then navigate
            onTicketButtonClick(showingId, purchaseReason, showing.contentId).then(() => {
                openingOnlineTicketUrlRef.current = false;
                router.push(ticketLink.href);
            });
        } else {
            // For external links, open window immediately to avoid iOS Safari blocking
            const baseUrl = new URL(ticketLink.href);
            baseUrl.searchParams.set('utm_source', process.env.NEXT_PUBLIC_ENV_CIN_DOMAIN || 'cineamo');
            baseUrl.searchParams.set('utm_medium', purchaseReason || 'unknown');

            // Open window immediately with base UTM params
            const newWindow = window.open(baseUrl.toString(), ticketLink.target);

            // Then update URL with purchase intent ID when available
            onTicketButtonClick(showingId, purchaseReason, showing.contentId)
                .then((ticketPurchaseIntentDto) => {
                    if (newWindow && !newWindow.closed) {
                        baseUrl.searchParams.set('utm_term', `ticketPurchaseIntent_${ticketPurchaseIntentDto?.id}`);
                        newWindow.location.href = baseUrl.toString();
                    }
                    openingOnlineTicketUrlRef.current = false;
                })
                .catch(() => {
                    openingOnlineTicketUrlRef.current = false;
                });

            if (newWindow) {
                newWindow.focus();
            }
        }
    }

    const ticketShape = (
        <TicketShape
            className={classnames(
                'w-full',
                ticketHeightProp,
                isTicketClickDisabled ? 'cursor-not-allowed' : 'cursor-pointer clickable-element'
            )}
            config={{
                fillColorHex: isTicketClickDisabled ? ticketItemUnavailableBgColorHex : ticketItemBgColorHex,
                fillColorOpacity: isTicketClickDisabled ? ticketItemUnavailableBgOpacity : ticketItemBgOpacity,
                cornerRadius: cornerRadius ?? 4
            }}>
            <div className="relative h-full flex flex-row">
                <div
                    className={classnames(
                        'flex w-full items-center justify-center z-10',
                        'text-body-bold-16 whitespace-nowrap space-x-8',
                        // With a custom style the color is applied inline below; the disabled
                        // variant keeps its class either way (it dims via `opacity-50`).
                        isTicketClickDisabled ? ticketItemUnavailableTextColor : !hasCustomStyle && ticketItemTextColor
                    )}
                    style={hasCustomStyle ? { color: ticketItemTextColor } : undefined}>
                    {props.children || t('common.tickets')}
                </div>
            </div>
        </TicketShape>
    );

    return (
        <div className="relative">
            {noOnlineTicketUrlIsOpen && (
                <NoOnlineTicketUrlModal
                    modalIsOpen={noOnlineTicketUrlIsOpen}
                    setModalIsOpen={setNoOnlineTicketUrlIsOpen}
                />
            )}
            <button
                className={classnames('flex items-center', props.className)}
                onClick={(e) => {
                    e.preventDefault();
                    track('Ticket Button Clicked', {
                        ...(trackingData ?? {}),
                        cinemaId: showing.cinemaId ?? '',
                        showingId: showing.id,
                        showingName: showing.name,
                        contentId: showing.contentId,
                        hasOnlineTicketUrl: ticketLink.hasOnlineTicketUrl ? 'Yes' : 'No',
                        hoursUntilShowing: getHoursUntilShowing(datetime.toString()) || 'N/A'
                    });
                    if (isTicketClickDisabled) {
                        return;
                    }
                    if (ticketLink.hasOnlineTicketUrl) {
                        onTicketClick?.(showing, ticketLink.target === '_blank');
                        onTicketButtonClickHandler();
                    } else {
                        setNoOnlineTicketUrlIsOpen(true);
                    }
                }}>
                {ticketShape}
            </button>
        </div>
    );
}
